import { isTieredVariant, isPremiumVariant } from '@lub0v/santa-utils';
import { premiumFeatureTier } from './featureTier';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@src/state/hooks';
import { useLocation } from 'react-router-dom';
import { isDotCom } from '@src/site';
import { premiumOffer, setCurrentPremiumQuote, type PremiumQuote } from './offer';

type TierPrice = { code: string; offerCode?: string; amountMinor: number; currency: string };
const PremiumOfferContext = createContext<{ quote: PremiumQuote | null; displayQuote: PremiumQuote | null; failed: boolean; tiers: TierPrice[] }>({
  quote: null,
  displayQuote: null,
  failed: false,
  tiers: [],
});
export function PremiumOfferProvider({ children }: { children: ReactNode }) {
  const { user, status } = useAppSelector((state) => state.session);
  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);
  const quoteParams = new URLSearchParams();
  if (params.get('automaticPayment') !== 'false' && params.has('price')) {
    quoteParams.set('automaticPayment', 'true');
    quoteParams.set('price', params.get('price') || '');
  }
  const buyPage = pathname.replace(/\/+$/, '') === '/premium/buy';
  quoteParams.set('tier', buyPage
    ? ['tier_1', 'tier_2', 'tier_3'].includes(params.get('tier') || '') ? params.get('tier')! : 'tier_2'
    : 'tier_1');
  const priceQuery = quoteParams.size ? `?${quoteParams}` : '';
  const accountIdentity = user
    ? `${user.id}:${user.isGuest ? 'guest' : 'account'}:${user.isPremium ? 'premium' : 'free'}:${user.premium?.access.currentPaymentId ?? ''}`
    : 'anonymous';
  const identity = `${accountIdentity}:${priceQuery}`;
  const [resolved, setResolved] = useState<{ identity: string; accountIdentity: string; quote: PremiumQuote } | null>(null);
  const [tiers, setTiers] = useState<TierPrice[]>([]);
  const [failedIdentity, setFailedIdentity] = useState<{ identity: string } | null>(null);
  const quote = status === 'ready' && resolved?.identity === identity ? resolved.quote : null;
  // Keep presentation stable during a tier refresh, but never reuse a stale checkout quote.
  const displayQuote = status === 'ready' && resolved?.accountIdentity === accountIdentity
    && resolved.quote.currency === (isDotCom() ? 'USD' : 'RUB') ? resolved.quote : null;
  const currency = isDotCom() ? 'USD' : 'RUB';
  const variant = displayQuote?.variant;
  useEffect(() => {
    if (!isTieredVariant(variant)) return;
    const controller = new AbortController();
    void fetch('/api/premium/tiers', { credentials: 'same-origin', signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('Catalog unavailable');
        const data: TierPrice[] = await response.json();
        if (!Array.isArray(data) || data.length !== 3 || data.some((tier, index) => tier.code !== `tier_${index + 1}` || tier.currency !== currency || !Number.isSafeInteger(tier.amountMinor) || tier.amountMinor <= 0)) throw new Error('Invalid catalog');
        if (!controller.signal.aborted) setTiers(data);
      }).catch(() => { if (!controller.signal.aborted) setTiers([]); });
    return () => controller.abort();
  }, [variant, currency]);
  useEffect(() => {
    setCurrentPremiumQuote(quote);
  }, [quote]);
  useEffect(() => {
    if (status !== 'ready') return;
    let active = true;
    const controller = new AbortController();
    void fetch(`/api/premium/offer${priceQuery}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Offer unavailable');
        const data: PremiumQuote = await response.json();
        const currency = isDotCom() ? 'USD' : 'RUB';
        if (
          !isPremiumVariant(data.variant) ||
          !Number.isInteger(data.amountMinor) ||
          data.amountMinor < 0 ||
          data.currency !== currency
        )
          throw new Error('Invalid offer');
        if (active) { setFailedIdentity(null); setResolved({ identity, accountIdentity, quote: data }); }
      })
      .catch(() => {
        if (active) setFailedIdentity({ identity });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [identity, accountIdentity, status, priceQuery]);
  return (
    <PremiumOfferContext.Provider
      value={{ quote, displayQuote, tiers, failed: failedIdentity !== null && failedIdentity.identity === identity }}
    >
      {children}
    </PremiumOfferContext.Provider>
  );
}
export function usePremiumOffer(feature?: string) {
  const user = useAppSelector(state => state.session.user);
  const { i18n } = useTranslation();
  const { quote, displayQuote, tiers, failed } = useContext(PremiumOfferContext);
  const featureTier = isTieredVariant(displayQuote?.variant) ? premiumFeatureTier(feature) : undefined;
  if (featureTier) {
    const tier = tiers.find(item => item.code === featureTier && item.currency === displayQuote?.currency);
    const access = user?.premium?.offers.find(item => item.tier === featureTier);
    const featureQuote = tier && displayQuote ? { ...displayQuote, amountMinor: access?.amountDueMinor ?? tier.amountMinor, offerCode: tier.offerCode ?? tier.code } : null;
    return { ...premiumOffer(i18n.language, featureQuote), variant: displayQuote?.variant, failed };
  }
  const current = premiumOffer(i18n.language, quote);
  const display = premiumOffer(i18n.language, displayQuote);
  return {
    ...current,
    variant: display.variant,
    symbolPrice: display.symbolPrice,
    formattedPrice: display.formattedPrice,
    paymentsAvailable: display.paymentsAvailable,
    failed,
  };
}
